import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useContext } from "react";
import { Dropdown } from './Dropdown';
import { UXPContext } from '@core/context/Context';
import IconButton from "@widget/buttons/IconButton";
import classNames from 'classnames';
import ButtonComponent from "@widget/buttons/ButtonComponent";
/**
 * @export
 * Displays a filter button which, when clicked, opens a popup panel.
 * Suitable for hiding filters for widgets or searches.
 * Uses Dropdown component for positioning with v5 styling patterns.
 *
 * @example Basic filter panel
 * ```tsx
 * <FilterPanel>
 *   <FormField>
 *     <Label>Category</Label>
 *     <Select options={categories} selected={category} onChange={setCategory} />
 *   </FormField>
 * </FilterPanel>
 * ```
 *
 * @example With clear functionality
 * ```tsx
 * <FilterPanel
 *   enableClear={hasFilters}
 *   onClear={() => {
 *     setCategory(null);
 *     setDateRange(null);
 *   }}
 * >
 *   <FormField>
 *     <Label>Sort By</Label>
 *     <Select options={sortOptions} selected={sortBy} onChange={setSortBy} />
 *   </FormField>
 *   <FormField>
 *     <Label>Date Range</Label>
 *     <DateRangePicker value={dateRange} onChange={setDateRange} />
 *   </FormField>
 * </FilterPanel>
 * ```
 *
 * @example Custom position and icon
 * ```tsx
 * <FilterPanel
 *   position="bottom-left"
 *   icon="sort"
 *   enableClear={true}
 *   onClear={handleClear}
 * >
 *   <FormField>
 *     <Label>Status</Label>
 *     <Select options={statusOptions} selected={status} onChange={setStatus} />
 *   </FormField>
 * </FilterPanel>
 * ```
 */
const FilterPanel = ({ onOpen, onClose, onClear, position = 'bottom-right', className, enableClear = false, icon = 'filter', children }) => {
    const context = useContext(UXPContext);
    const [isOpen, setIsOpen] = useState(false);
    const handleOpen = useCallback(() => {
        setIsOpen(true);
        if (typeof onOpen === "function") {
            onOpen();
        }
    }, [onOpen]);
    const handleClose = useCallback(() => {
        setIsOpen(false);
        if (typeof onClose === "function") {
            onClose();
        }
    }, [onClose]);
    const handleClear = useCallback(() => {
        if (typeof onClear === "function") {
            onClear();
        }
    }, [onClear]);
    // Trigger button
    const trigger = (_jsx(IconButton, { type: icon, variant: "secondary", size: "medium" }));
    // Filter panel content
    const panelContent = (_jsxs("div", { className: classNames("uxpcore_filterpanel__content", className), children: [_jsxs("div", { className: "uxpcore_filterpanel__header", children: [_jsxs("div", { className: "uxpcore_filterpanel__title", children: [_jsx(IconButton, { type: "filter", variant: "secondary", size: "small", disabled: true }), _jsx("span", { className: "uxpcore_filterpanel__title-text", children: context.$L('uxp-core.text.filters') })] }), _jsx(IconButton, { type: "close", variant: "secondary", size: "small", onClick: handleClose })] }), _jsx("div", { className: "uxpcore_filterpanel__body", children: children }), _jsx("div", { className: "uxpcore_filterpanel__footer", children: _jsx(ButtonComponent, { title: context.$L('uxp-core.text.clear'), onClick: handleClear, disabled: !enableClear, variant: "secondary", size: 'small' }) })] }));
    return (_jsx(Dropdown, { trigger: trigger, content: panelContent, position: position, contentClassName: "uxpcore_filterpanel", isOpen: isOpen, onToggle: handleOpen }));
};
export default FilterPanel;
